<template>
  <!-- eslint-disable max-len -->
  <div class="webhooks">
    <div class="page-topbar">
      <div class="page-topbar__title">
        Webhook Configuration
      </div>
    </div>
    <tab-nav :tabs="navItems" />
    <div class="mb-2">
      Webhooks are a convient way to integrate and send BioConnect Link event data to external
      applications.
    </div>
    <template v-if="loading">
      <div class="empty-state">
        <loader size="100" />
      </div>
    </template>
    <template v-else>
      <!-- ADD NEW WEBHOOK ROW -->
      <div class="webhooks-item">
        <div class="form-container">
          <div class="form-label-container">
            <label class="form-label">Name</label>
            <div class="form__error">
              {{ newWebhook.nameErrorMessage }}
            </div>
          </div>
          <input
            v-model="newWebhook.name"
            data-testid="name-input-webhook"
            class="form-field"
            placeholder="Application name..."
          />
        </div>
        <div class="form-container">
          <div class="form-label-container">
            <label class="form-label">Callback URL</label>
            <div class="form__error">
              {{ newWebhook.urlErrorMessage }}
            </div>
          </div>
          <input
            v-model="newWebhook.url"
            data-testid="url-input-webhook"
            class="form-field"
            placeholder="https://example.com..."
          />
        </div>
        <div class="form-container">
          <label class="form-label">Application Type</label>
          <select
            v-model="newWebhook.type"
            class="form-field select-dropdown"
            data-testid="type-dropdown-webhook"
          >
            <option
              v-for="type in webhookTypes"
              :key="type.value"
              :value="type.value"
            >
              {{ type.label }}
            </option>
          </select>
        </div>
        <template v-if="newWebhook.isLoading">
          <loader :size="30" />
        </template>
        <template v-else>
          <button
            class="webhooks__create btn"
            data-testid="create-button-webhook"
            @click="addWebhook()"
          >
            Add Webhook
          </button>
        </template>
      </div>
      <!-- CREATED WEBHOOKS -->
      <template v-for="webhook in webhooks">
        <div
          :key="webhook.uuid"
          class="webhooks-item"
        >
          <div class="form-container">
            <div class="form-label-container">
              <label class="form-label">Name</label>
              <div class="form__error">
                {{ webhook.nameErrorMessage }}
              </div>
            </div>
            <input
              v-model="webhook.name"
              data-testid="name-input-webhook-created"
              :disabled="!webhook.isEditMode"
              class="form-field"
            />
          </div>
          <div class="form-container">
            <div class="form-label-container">
              <label class="form-label">Callback URL</label>
              <div class="form__error">
                {{ webhook.urlErrorMessage }}
              </div>
            </div>
            <input
              v-model="webhook.url"
              data-testid="url-input-webhook-created"
              :disabled="!webhook.isEditMode"
              class="form-field"
            />
          </div>
          <div class="form-container">
            <label class="form-label">Application Type</label>
            <select
              v-model="webhook.type"
              :disabled="!webhook.isEditMode"
              class="form-field select-dropdown"
              data-testid="type-dropdown-webhook-created"
            >
              <option
                v-for="type in webhookTypes"
                :key="type.value"
                :value="type.value"
              >
                {{ type.label }}
              </option>
            </select>
          </div>
          <toggle
            v-tooltip="{
              content: `${webhook.isActive ? 'Disable' : 'Enable'} Webhook`,
            }"
            :toggle.sync="webhook.isActive"
            @toggle-clicked="toggleWebhook(webhook)"
          >
          </toggle>
          <template v-if="webhook.isEditMode">
            <template v-if="webhook.isLoading">
              <loader :size="30" />
            </template>
            <template v-else>
              <button
                v-tooltip="{ content: 'Delete Webhook' }"
                class="btn btn--red btn__outline btn__outline-icon"
                data-testid="delete-button-webhook-edit"
                @click="deleteWebhook(webhook)"
              >
                <span v-html="deleteIcon" />
              </button>
              <button
                v-tooltip="{ content: 'Cancel Edit' }"
                class="btn btn--grey btn__outline btn__outline-icon"
                data-testid="cancel-button-webhook-edit"
                @click="cancelEdit(webhook)"
              >
                <span v-html="cancelIcon" />
              </button>
              <button
                v-tooltip="{ content: 'Save Changes' }"
                class="btn btn__outline btn__outline-icon"
                data-testid="save-button-webhook-edit"
                @click="updateWebhook(webhook)"
              >
                <span v-html="saveIcon" />
              </button>
            </template>
          </template>
          <template v-else>
            <button
              v-tooltip="{ content: 'Send Test Data' }"
              class="webhooks__test btn btn__outline"
              data-testid="test-button-webhook-created"
              @click="testWebhook(webhook)"
            >
              Test
            </button>
            <button
              v-tooltip="{ content: 'Edit' }"
              class="btn btn__outline btn__outline-icon"
              data-testid="edit-button-webhook-created"
              @click="setEdit(webhook)"
            >
              <span v-html="editIcon" />
            </button>
          </template>
        </div>
      </template>
    </template>
  </div>
  <!-- eslint-enable max-len -->
</template>

<script>
import feather from 'feather-icons'

import TabNav from '@/components/tabNav.vue'
import Loader from '@/components/loader.vue'
import Toggle from '@/components/toggle.vue'

import configurationNavMixin from '@/mixins/configurationNavMixin'

import webhookQueries from '@/graphql/webhooks.js'
import REGEX from '@/global/utilities.js'

export default {
  name: 'WebhookConfiguration',
  components: {Loader, TabNav, Toggle},
  mixins: [configurationNavMixin, webhookQueries],
  data() {
    return {
      loading: false,
      webhooks: [],
      webhookTypes: [
        {
          label: 'Slack (Wellness Events Only)',
          value: 'slack',
        },
        {
          label: 'Access Logs',
          value: 'transactions',
        },
        {
          label: 'Microsoft Teams',
          value: 'teams',
        },
      ],
      newWebhook: {
        name: '',
        url: '',
        isActive: true,
        isLoading: false,
        type: 'transactions',
        description: 'New Webhook',
        nameErrorMessage: null,
        urlErrorMessage: null,
      },
      paginationParams: {
        page: 1,
        perPage: 100,
      },
      editIcon: feather.icons['edit-2'].toSvg({width: '1.3em'}),
      deleteIcon: feather.icons['trash-2'].toSvg({width: '1.3em'}),
      cancelIcon: feather.icons.x.toSvg({width: '1.5em'}),
      saveIcon: feather.icons.check.toSvg({width: '1.5em'}),
    }
  },
  created() {
    this.fetchWebhooks()
  },
  methods: {
    async fetchWebhooks() {
      try {
        this.loading = true
        const _webhooks = await this.listWebhooksQuery(this.paginationParams)
        this.loading = false
        this.webhooks = _webhooks.map((webhook) => {
          return {
            ...webhook,
            isEditMode: false,
            isLoading: false,
            nameErrorMessage: null,
            urlErrorMessage: null,
          }
        })
      } catch (error) {
        this.loading = false
        console.log(error)
        this.displayAlert('fail', 'Unable to fetch webhooks')
      }
    },
    async addWebhook() {
      if (this.webhookValidation(this.newWebhook)) {
        this.newWebhook.isLoading = true
        try {
          await this.createWebhookMutation(this.newWebhook)
          this.newWebhook.isLoading = false
          this.displayAlert('success', 'Webhook Created')
          this.fetchWebhooks()
          this.newWebhook.name = ''
          this.newWebhook.url = ''
        } catch (error) {
          console.log(error)
          this.newWebhook.isLoading = false
          this.displayAlert('fail', 'Unable to create webhook')
        }
      }
    },
    async updateWebhook(webhook) {
      if (this.webhookValidation(webhook)) {
        webhook.isLoading = true
        try {
          await this.updateWebhookMutation(webhook)
          this.displayAlert('success', 'Webhook Updated')
          webhook.isLoading = false
          webhook.isEditMode = false
        } catch (error) {
          console.log(error)
          webhook.isLoading = false
          this.displayAlert('fail', 'Unable to update webhook')
        }
      }
    },
    async toggleWebhook(webhook) {
      webhook.isActive = !webhook.isActive
      try {
        const {isActive} = await this.updateWebhookMutation(webhook)
        const _message = isActive ? 'Webhook Enabled' : 'Webhook Disabled'
        this.displayAlert('success', _message)
      } catch (error) {
        webhook.isActive = !webhook.isActive
        this.displayAlert('fail', 'Unable to update webhook')
        console.log(error)
      }
    },
    async deleteWebhook(webhook) {
      webhook.isLoading = true
      try {
        await this.deleteWebhookMutation(webhook)
        webhook.isLoading = false
        this.displayAlert('success', 'Webhook Deleted')
        this.fetchWebhooks()
      } catch (error) {
        console.log(error)
        webhook.isLoading = false
        this.displayAlert('fail', 'Unable to delete webhook')
      }
    },
    async testWebhook(webhook) {
      try {
        await this.testWebhookMutation(webhook)
        this.displayAlert('success', 'Published a message to the webhook!')
      } catch (error) {
        console.log(error)
        this.displayAlert('faile', 'Unable to send test data')
      }
    },
    webhookValidation(webhook) {
      const {name, url} = webhook
      webhook.nameErrorMessage = this.isAlphaNumeric(name)
        ? null
        : 'Only numbers and letters are allowed'
      webhook.urlErrorMessage = REGEX.url.test(url) ? null : 'Please provide a valid URL'
      return REGEX.url.test(url) && this.isAlphaNumeric(name)
    },
    setEdit(webhook) {
      webhook.isEditMode = true
      webhook.saveName = webhook.name
      webhook.saveURL = webhook.url
    },
    cancelEdit(webhook) {
      webhook.isEditMode = false
      webhook.name = webhook.saveName
      webhook.url = webhook.saveURL
      webhook.nameErrorMessage = ''
      webhook.urlErrorMessage = ''
    },
  },
}
</script>

<style lang="scss" scoped>
.webhooks {
  &-item {
    display: grid;
    grid-template-columns: 2fr 3fr 1fr 60px 60px 60px 60px;
    grid-gap: 1em;
    justify-items: end;
    align-items: center;
    margin: 1em 0;
  }
  &__create {
    grid-column-start: 5;
    grid-column-end: 8;
    width: 100%;
  }
  &__test {
    grid-column-start: 5;
    grid-column-end: 7;
    width: 100%;
  }
}
</style>
